import React from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "../utils";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Check, Star, Users, Clock, Utensils, Bed, Car, ShieldCheck, Bus, Coffee, Map, CheckCircle, Sparkles } from "lucide-react";
import { useLanguage } from "../layout";
import { motion } from "framer-motion";
import { useConfig } from "../contexts/ConfigContext";

export default function Packages() {
  const { language, isRTL } = useLanguage();

  const { getPackage, loading: configLoading } = useConfig();
  const standardPackage = getPackage("standard");
  const originalPackagePrice = standardPackage?.price_per_person || 1000;
  const packagePrice = 900; // Hanukkah special price
  const currency = standardPackage?.currency || "ILS";
  const symbols = { ILS: "₪", USD: "$", EUR: "€" };
  const symbol = symbols[currency] || "₪";
  const formattedPrice = `${symbol}${packagePrice.toLocaleString()}`;
  const originalFormattedPrice = `${symbol}${originalPackagePrice.toLocaleString()}`;


  const content = {
    he: {
      title: "תמחור שקוף",
      subtitle: "אצלנו אין אותיות קטנות",
      priceStatement: "מבצע חנוכה! החל מ־{price} לאדם",
      transparency: "מבצע מיוחד לחנוכה! חבילת הסטנדרט היא כעת {price} לאדם (במקום {originalPrice}) וכוללת את כל מה שרשום למטה",
      bookNow: "הזמן את המקום שלך",
      perPerson: "לאדם",
      included: "מה כלול בחבילה",
      whyChoose: "למה לבחור בנו?",
      professionalExperience: "חוויה מקצועית ומאורגנת",
      package: {
        name: "חבילת סטנדרט טרק נחל פרת",
        description: "חבילה מקיפה ושקופה הכוללת את כל מה שצריך לחוויית טיול בלתי נשכחת במדבר יהודה",
        features: [
          {
            icon: Bus,
            title: "העברות וציוד",
            description: "אתם סוחבים רק מה שהכרחי"
          },
          {
            icon: Bed,
            title: "לינה בצימר",
            description: "2 לילות בצימרים ברמת סטנדרט"
          },
          {
            icon: Utensils,
            title: "ארוחות כשרות",
            description: "ארוחות בוקר וערב + צידה לדרך למהלך כל היום"
          },
          {
            icon: ShieldCheck,
            title: "ביטול חינם",
            description: "עד 3 ימים לפני הטיול - ביטול חינם"
          },
          {
            icon: Coffee,
            title: "שוברים לקפה",
            description: "שוברים לעגלת קפה מקומית בדרך"
          },
          {
            icon: Map,
            title: "מפה והסברים",
            description: "תקבלו מאיתנו מפה והסברים מפורטים על הדרך"
          }
        ]
      }
    },
    en: {
      title: "Transparent Pricing",
      subtitle: "No fine print here",
      priceStatement: "Hanukkah Special! Starting from {price} per person",
      transparency: "Special Hanukkah offer! The standard package is now {price} per person (instead of {originalPrice}) and includes everything listed below.",
      bookNow: "Book Your Spot",
      perPerson: "per person",
      included: "What's Included",
      whyChoose: "Why Choose Us?",
      professionalExperience: "Professional and organized experience",
      package: {
        name: "Nahal Prat Trek Standard Package",
        description: "Comprehensive and transparent package including everything you need for an unforgettable Judean Desert experience",
        features: [
          {
            icon: Bus,
            title: "Transfers & Equipment",
            description: "You only carry what’s essential"
          },
          {
            icon: Bed,
            title: "Cabin Accommodation",
            description: "2 nights in standard cabins"
          },
          {
            icon: Utensils,
            title: "Kosher Meals",
            description: "Breakfast and dinner + trail snacks throughout the day"
          },
          {
            icon: ShieldCheck,
            title: "Free Cancellation",
            description: "Up to 3 days before the trek - no fees"
          },
          {
            icon: Coffee,
            title: "Coffee Vouchers",
            description: "Vouchers for local coffee cart along the way"
          },
          {
            icon: Map,
            title: "Trail Map & Info",
            description: "We provide a detailed map and route information for self-guiding"
          }
        ]
      }
    }
  };

  const currentContent = content[language];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="min-h-screen bg-desert-50 overflow-x-hidden">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-desert-100 to-desert-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-w-0">
          <motion.div 
            className="text-center min-w-0"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm text-desert-700 font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              {currentContent.professionalExperience}
            </motion.div>
            
            <motion.h1 variants={itemVariants} className="text-5xl md:text-6xl font-display font-bold text-gray-900 mb-6">
              {currentContent.title}
            </motion.h1>
            
            <motion.p variants={itemVariants} className="text-2xl text-gray-700 mb-4 font-medium">
              {currentContent.subtitle}
            </motion.p>
            
            {!configLoading && (
              <>
                <motion.div variants={itemVariants} className="mb-4">
                  <div className="text-2xl text-gray-500 line-through mb-2">
                    {language === 'he' ? 'מחיר רגיל:' : 'Regular price:'} {originalFormattedPrice}
                  </div>
                  <div className="text-4xl font-bold text-desert-600">
                    {currentContent.priceStatement.replace("{price}", formattedPrice)}
                  </div>
                </motion.div>

                <motion.p
                  variants={itemVariants}
                  className="text-lg text-gray-600 max-w-2xl mx-auto"
                >
                  {currentContent.transparency.replace("{price}", formattedPrice).replace("{originalPrice}", originalFormattedPrice)}
                </motion.p>
              </>
            )}

          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 min-w-0">
        {/* Main Package Card */}
        <motion.div 
          className="max-w-4xl mx-auto mb-20 min-w-0"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
            <Card className="border-none shadow-2xl bg-desert-light backdrop-blur-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0">
                <div className="bg-desert-100/70 backdrop-blur-sm border-b border-desert-300/40 text-gray-900 text-center py-3">
                  <span className="font-semibold tracking-wide" style={{ fontSize: "clamp(1rem, 3vw, 1.2rem)" }}>
                    {currentContent.package.name}
                  </span>
                </div>
              </div>


            <CardHeader className="text-center pb-8 pt-20">
              <div className="mb-4">
                <div className="text-3xl text-gray-400 line-through mb-2">
                  {symbol}{originalPackagePrice.toLocaleString()}
                </div>
                <div className="text-7xl font-bold text-desert-600">
                  {symbol}{packagePrice.toLocaleString()}
                </div>
                <div className="inline-block bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold mt-2">
                  {language === 'he' ? 'מבצע חנוכה!' : 'Hanukkah Special!'}
                </div>
              </div>
              <p className="text-xl text-gray-500 mb-6">{currentContent.perPerson}</p>
              <p className="text-xl text-gray-700 leading-relaxed max-w-2xl mx-auto">
                {currentContent.package.description}
              </p>
            </CardHeader>

            <CardContent className="px-8 pb-12">
              <div className="text-center mb-10">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{currentContent.included}</h3>
                <div className="w-24 h-1 bg-[#c56f19] mx-auto rounded-full"></div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mb-10">
                {currentContent.package.features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="flex items-center gap-4 p-4 bg-desert-solid rounded-xl border border-desert-200/30 min-w-0"
                  >
                    <div className="w-12 h-12 bg-[#c56f19] rounded-xl flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="font-bold text-gray-900 mb-1 w-full">{feature.title}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed w-full">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="text-center"
              >
                <Link to={createPageUrl("Booking")}>
                  <Button className="group relative overflow-hidden bg-[#c56f19] text-white text-xl px-12 py-6 rounded-2xl shadow-warm-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 whitespace-nowrap">
                    <span className="relative z-10 flex items-center gap-3">
                      <CheckCircle className="w-6 h-6" />
                      {currentContent.bookNow}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </Button>
                </Link>
                
                <p className="text-sm text-gray-500 mt-4 flex items-center justify-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-green-500" />
                  {language === 'he' ? 'תשלום מאובטח' : 'Secure Payment'}
                </p>
              </motion.div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Trust & Transparency Section */}
        <motion.section 
          className="bg-desert-light rounded-3xl p-6 md:p-12 shadow-warm border border-desert-200/30"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
        >
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex justify-center mb-4 md:mb-6">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-[#c56f19] rounded-2xl flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </div>
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">
              {currentContent.whyChoose}
            </h2>
            
            <p className="text-lg md:text-xl text-gray-700 mb-6 md:mb-8 leading-relaxed px-2 md:px-0">
              {language === 'he' 
                ? 'שקיפות מלאה, שירות מקצועי, וחוויה בלתי נשכחת. אנחנו מאמינים בכנות מלאה ובמתן ערך אמיתי לכל שקל שאתם משלמים.'
                : 'Complete transparency, professional service, and an unforgettable experience. We believe in full honesty and providing real value for every shekel you pay.'
              }
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 text-center">
              <div className="p-3 md:p-4 min-w-0">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-2 md:mb-3 min-w-0">
                  <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base w-full">
                  {language === 'he' ? 'ללא עלויות נסתרות' : 'No Hidden Costs'}
                </h3>
                <p className="text-gray-600 text-sm w-full">
                  {language === 'he' ? 'מה שרואים זה מה שמשלמים' : 'What you see is what you pay'}
                </p>
              </div>
              
              <div className="p-3 md:p-4 min-w-0">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-2 md:mb-3 min-w-0">
                  <Star className="w-5 h-5 md:w-6 md:h-6 text-blue-600 fill-current" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base w-full">
                  {language === 'he' ? 'איכות מובטחת' : 'Guaranteed Quality'}
                </h3>
                <p className="text-gray-600 text-sm w-full">
                  {language === 'he' ? 'רמת שירות גבוהה בכל שלב' : 'High service level at every stage'}
                </p>
              </div>
              
              <div className="p-3 md:p-4 col-span-2 md:col-span-1 min-w-0">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-2 md:mb-3 min-w-0">
                  <Users className="w-5 h-5 md:w-6 md:h-6 text-orange-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base w-full">
                  {language === 'he' ? 'תמיכה בקהילה המקומית' : 'Supporting Local Community'}
                </h3>
                <p className="text-gray-600 text-sm w-full">
                  {language === 'he' ? 'תמיכה בעסקים המקומיים' : 'Supporting local businesses'}
                </p>
              </div>
            </div>

            <div className="mt-10">
              <Link to={createPageUrl("Booking")}>
                <Button size="lg" className="bg-[#c56f19] text-white hover:opacity-90 text-lg px-12 py-4 rounded-xl shadow-warm hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 whitespace-nowrap">
                  {language === 'he' ? 'בואו נתחיל!' : "Let's Get Started!"}
                </Button>
              </Link>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
